import React from 'react';

import { ClassName, GADataType, I18nText, UUID } from '../../../../../types';
import {
  SmartContractShareIteration,
  SmartContractShareStatus,
  UpdateSmartContractSharesCacheKeys
} from '../../../smartContractSharesTypes';
import { TaskNanoID } from '../../../../tasks/tasksTypes';
import { SmartContractID } from '../../../../smartContracts/smartContractsTypes';
import {
  UserClient,
  UserFullName,
  UserID,
  UserImageFile
} from '../../../../users/usersTypes';
import { CompanyEventTemplateID } from '../../../../companyEventTemplates/companyEventTemplatesTypes';

import { IconsEnum } from '../../../../../assets/icons/types';
import { CreateSmartContractShareFormView } from '../../forms/CreateSmartContractShareForm/CreateSmartContractShareForm.types';

import { useCreateSmartContractShareForm } from '../../forms/CreateSmartContractShareForm/hooks/useCreateSmartContractShareForm';

import { CreateSmartContractShareForm } from '../../forms/CreateSmartContractShareForm';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { SimpleModalButton } from '../../../../../helpers/buttons/SimpleModalButton';

import { usersKeys, words } from '../../../../../locales/keys';

interface CreateSmartContractShareModalButtonProps {
  taskNanoId: TaskNanoID;
  invitedUserUuids?: UUID[];
  iteration?: SmartContractShareIteration;
  smartContractId: SmartContractID;
  status: SmartContractShareStatus;
  className?: ClassName;
  icon?: IconsEnum;
  iconClassName?: ClassName;
  i18nText?: I18nText;
  i18nTextClassName?: ClassName;
  i18nTitle?: I18nText;
  tooltipI18nText?: I18nText;
  cacheKeys?: UpdateSmartContractSharesCacheKeys;
  defaultUsers?: {
    id: UserID;
    client?: UserClient;
    fullName: UserFullName;
    image?: {
      file: UserImageFile;
    };
    currentTeam?: {
      nanoId: string;
    };
  }[];
  disabled?: boolean;
  afterCreate?: () => void;
  withoutEventIds?: CompanyEventTemplateID[];
}

const CREATE_SMART_CONTRACT_SHARE_FORM = 'create-smart-contract-share-form';

function CreateSmartContractShareModalButton({
  taskNanoId,
  invitedUserUuids,
  iteration,
  smartContractId,
  status,
  className,
  icon,
  iconClassName,
  i18nText,
  i18nTextClassName,
  i18nTitle = usersKeys.invite,
  tooltipI18nText,
  cacheKeys,
  defaultUsers,
  disabled,
  afterCreate,
  withoutEventIds,
  dataGa
}: CreateSmartContractShareModalButtonProps & GADataType) {
  const {
    control,
    createSmartContractShareErrorMessage,
    inviteUsersToTaskErrorMessage,
    createSmartContractShareLoading,
    inviteUsersToTaskLoading,
    handleResetCreateSmartContractShareForm,
    setValue,
    formView,
    goToSelectUsersView,
    disabledSubmit,
    oneFormView,
    withoutEditUsers,
    getValues,
    handleSubmit
  } = useCreateSmartContractShareForm({
    taskNanoId,
    iteration,
    smartContractId,
    status,
    cacheKeys,
    defaultUsers,
    afterCreate,
    withoutEventIds
  });

  return (
    <SimpleModalButton
      i18nText={i18nText}
      i18nTextClassName={i18nTextClassName}
      className={
        className ||
        'flex text-left dark:hover:bg-gray-800 hover:bg-gray-100 px-4 py-2 text-sm w-full whitespace-nowrap'
      }
      icon={icon}
      iconClassName={iconClassName}
      i18nSubmitText={
        formView === CreateSmartContractShareFormView.CHANGE_SHARE ||
        oneFormView
          ? words.submit
          : words.next
      }
      i18nCancelText={
        formView === CreateSmartContractShareFormView.CHANGE_SHARE &&
        !oneFormView
          ? words.back
          : words.cancel
      }
      i18nTitle={
        formView === CreateSmartContractShareFormView.SELECT_USERS && i18nTitle
      }
      tooltipI18nText={tooltipI18nText}
      isLoading={createSmartContractShareLoading || inviteUsersToTaskLoading}
      onOpen={handleResetCreateSmartContractShareForm}
      closeOnCancel={
        formView === CreateSmartContractShareFormView.SELECT_USERS ||
        oneFormView
      }
      onCancel={
        formView === CreateSmartContractShareFormView.CHANGE_SHARE &&
        !oneFormView
          ? goToSelectUsersView
          : undefined
      }
      cancelButtonPosition={
        formView === CreateSmartContractShareFormView.CHANGE_SHARE &&
        !oneFormView
          ? 'start'
          : 'end'
      }
      submitDisabled={disabledSubmit}
      onSubmit={handleSubmit}
      notHideOnSubmit={
        formView === CreateSmartContractShareFormView.SELECT_USERS &&
        !oneFormView
      }
      childrenClassName="overflow-y-auto relative"
      disabled={disabled}
      dataGa={dataGa}
    >
      <CreateSmartContractShareForm
        form={CREATE_SMART_CONTRACT_SHARE_FORM}
        isLoading={createSmartContractShareLoading || inviteUsersToTaskLoading}
        control={control}
        formView={formView}
        goToSelectUsersView={
          formView === CreateSmartContractShareFormView.CHANGE_SHARE &&
          !oneFormView
            ? goToSelectUsersView
            : undefined
        }
        i18nTitle={i18nTitle}
        setValue={setValue}
        invitedUserUuids={invitedUserUuids}
        withoutEditUsers={withoutEditUsers}
        getValues={getValues}
        withSelectEvents
        status={status}
      />

      <div className="px-4">
        <AlertMessage
          message={
            createSmartContractShareErrorMessage ||
            inviteUsersToTaskErrorMessage
          }
        />
      </div>
    </SimpleModalButton>
  );
}

export default CreateSmartContractShareModalButton;
